import React, { useState } from 'react';
import axios from 'axios';
import { AlertCircle, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * B-53 security review: Google only vouches for addresses it hosts (Gmail, or
 * a Workspace domain). For any other address that already has a Marakin
 * account, the server has emailed the account a 6-digit code (the same B-59
 * code) and nothing is linked until it is entered here.
 *
 *   pending — the /auth/social reply { linkTicket, email, provider, retryAfter?, limit? }
 *   onDone(user) — signed in, Google now linked
 *   onCancel()   — back to the page's normal form
 */
const SocialLinkCodeStep = ({ pending, onDone, onCancel }) => {
  const { confirmSocialLink } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState(pending.limit ? 'That\'s a lot of codes for one day. Try again tomorrow, or use your password.' : '');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.replace(/\s/g, ''))) { setError('Enter the 6-digit code from the email.'); return; }
    setError(''); setNote(''); setLoading(true);
    try {
      const user = await confirmSocialLink({ linkTicket: pending.linkTicket, code });
      await onDone(user);
    } catch (err) {
      if (err?.response?.data?.code === 'social_ticket_invalid') setExpired(true);
      setError(err?.response?.data?.error || 'That didn\'t work. Please try again.');
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setError(''); setNote('');
    try {
      await axios.post('/auth/social/link-code/resend', { linkTicket: pending.linkTicket });
      setNote('We\'ve sent a new code.');
    } catch (err) {
      if (err?.response?.data?.code === 'social_ticket_invalid') setExpired(true);
      setError(err?.response?.data?.error || 'We couldn\'t send a new code. Please try again.');
    }
  };

  return (
    <>
      <h1>Check your email</h1>
      <p className="auth-subtitle">
        There's already a Marakin account for <strong>{pending.email}</strong>. To connect your Google account to it,
        enter the 6-digit code we've just emailed to that address.
      </p>
      {note && <p className="social-note">{note}</p>}
      {error && <div className="auth-error" role="alert"><AlertCircle size={16} /> {error}</div>}
      {expired ? (
        <button type="button" className="btn btn-primary auth-submit" onClick={onCancel}>Start again</button>
      ) : (
        <form onSubmit={submit} className="auth-form">
          <div className="form-group">
            <label className="label">Code</label>
            <input className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={7}
              value={code} onChange={e => setCode(e.target.value)} placeholder="123456" required />
          </div>
          <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
            {loading ? <span className="spinner" /> : <><LogIn size={16} /> Confirm and sign in</>}
          </button>
        </form>
      )}
      <p className="auth-switch">
        {!expired && <><button type="button" className="social-back" onClick={resend}>Send a new code</button> · </>}
        <button type="button" className="social-back" onClick={onCancel}>Back</button>
      </p>
    </>
  );
};

export default SocialLinkCodeStep;
